import React, { useState } from 'react';
import {
    Box,
    IconButton,
    ImageList,
    ImageListItem,
    useTheme,
    useMediaQuery,
    Typography,
    Stack
} from '@mui/material';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';

const ImageGallery = ({ images, height = 450, singleImage = false }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handlePrevious = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const handleThumbnailClick = (index) => {
        setCurrentIndex(index);
    };

    if (!images || images.length === 0) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'grey.100'
                }}
            >
                <img
                    src="/placeholder.jpg"
                    alt="No image available"
                    style={{ height: '100%', objectFit: 'cover' }}
                />
            </Box>
        );
    }

    if (singleImage) {
        return (
            <Box sx={{ position: 'relative', width: '100%', height }}>
                <img
                    src={images[currentIndex]}
                    alt={`Room ${currentIndex + 1}`}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                />
                {images.length > 1 && (
                    <>
                        {currentIndex > 0 && (
                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    left: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                                }}
                                onClick={handlePrevious}
                            >
                                <NavigateBefore />
                            </IconButton>
                        )}
                        {currentIndex < images.length - 1 && (
                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                                }}
                                onClick={handleNext}
                            >
                                <NavigateNext />
                            </IconButton>
                        )}
                        <Typography
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                right: 16,
                                bgcolor: 'rgba(0, 0, 0, 0.6)',
                                color: 'white',
                                px: 1,
                                borderRadius: 1
                            }}
                        >
                            {currentIndex + 1} / {images.length}
                        </Typography>
                    </>
                )}
            </Box>
        );
    }

    const mainHeight = isMobile ? height * 0.8 : height * 0.85;
    const thumbnailHeight = isMobile ? height * 0.2 : height * 0.15;
    const thumbnailWidth = isMobile ? 80 : 100;

    return (
        <Stack spacing={1} sx={{ width: '100%', height }}>
            {/* メイン画像 */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: mainHeight,
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        display: 'block',
                        paddingTop: '56.25%', // 16:9のアスペクト比
                    }
                }}
            >
                <img
                    src={images[currentIndex]}
                    alt={`Room ${currentIndex + 1}`}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        transition: 'transform 0.3s ease-in-out'
                    }}
                />
                {images.length > 1 && (
                    <>
                        <IconButton
                            sx={{
                                position: 'absolute',
                                left: isMobile ? 8 : 16,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    transform: 'translateY(-50%) scale(1.1)'
                                },
                                transition: 'transform 0.2s'
                            }}
                            onClick={handlePrevious}
                        >
                            <NavigateBefore />
                        </IconButton>
                        <IconButton
                            sx={{
                                position: 'absolute',
                                right: isMobile ? 8 : 16,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    transform: 'translateY(-50%) scale(1.1)'
                                },
                                transition: 'transform 0.2s'
                            }}
                            onClick={handleNext}
                        >
                            <NavigateNext />
                        </IconButton>
                        <Typography
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                right: 16,
                                bgcolor: 'rgba(0, 0, 0, 0.6)',
                                color: 'white',
                                px: 1,
                                borderRadius: 1
                            }}
                        >
                            {currentIndex + 1} / {images.length}
                        </Typography>
                    </>
                )}
            </Box>

            {/* サムネイル */}
            <Box
                sx={{
                    width: '100%',
                    minHeight: thumbnailHeight,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    pb: 1
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        width: '100%',
                        px: 1,
                        py: 0.5,
                        '&::-webkit-scrollbar': {
                            height: 6
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: 3
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: 3,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.3)'
                            }
                        }
                    }}
                >
                    {images.map((image, index) => (
                        <Box
                            key={index}
                            onClick={() => handleThumbnailClick(index)}
                            sx={{
                                cursor: 'pointer',
                                opacity: index === currentIndex ? 1 : 0.6,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    opacity: 0.8,
                                    transform: 'scale(1.05)'
                                },
                                position: 'relative',
                                width: thumbnailWidth,
                                height: thumbnailWidth * 0.75, // 4:3の縦横比を反転（横長に）
                                flexShrink: 0,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: index === currentIndex ? 2 : 1,
                                borderColor: index === currentIndex ? 'primary.main' : 'divider',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <img
                                src={image}
                                alt={`Thumbnail ${index + 1}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                                loading="lazy"
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
        </Stack>
    );
};

export default ImageGallery; 